import { VideoEditor } from '../VideoEditor';
import { Element } from '../models/Element';
import { VideoEditorLogger } from '../../utility/VideoEditorLogger';
import { EditorChangeType } from '../types';
import { AbstractLayoutCommand } from './AbstractLayoutCommand';
import { ClearLayoutElementsCommand } from './ClearLayoutElementsCommand';
import { Command } from './Command';
import { AddLayoutElementsCommand } from './AddLayoutElementsCommand';

export interface SwitchLayoutElementParameters {
  layoutType: 'stacked-1-1' | 'stacked-1-3';
  videoSourceWidthPrev?: number;
  videoSourceWidthCurr?: number;
}

export interface ResultElements {
  top: Element;
  bottom: Element;
  parent: Element;
}

export class SwitchLayoutTypeCommand extends AbstractLayoutCommand {
  private params: SwitchLayoutElementParameters;
  private _resultElements: ResultElements | null = null;

  constructor(
    editor: VideoEditor,
    element: Element,
    params: SwitchLayoutElementParameters,
  ) {
    super(editor, element);
    this.params = params;
  }

  execute(): boolean {
    if (!this.sourceElement) {
      VideoEditorLogger.logCommand(this, 'error', {
        error: 'No source element provided',
      });
      return false;
    }

    super.execute();

    const { videoSourceWidthPrev, videoSourceWidthCurr, layoutType } =
      this.params;

    let command: Command = new ClearLayoutElementsCommand(
      this.editor,
      this.sourceElement,
      {
        fit: 'cover',
        videoSourceWidthPrev,
        videoSourceWidthCurr,
      },
    );
    let success = command.execute();
    if (!success) {
      VideoEditorLogger.logCommand(this, 'error', {
        error: 'Switch: failed to clear layout elements',
      });
      return false;
    }

    command = new AddLayoutElementsCommand(this.editor, this.sourceElement, {
      layoutType,
    });
    success = command.execute();
    if (!success) {
      VideoEditorLogger.logCommand(this, 'error', {
        error: 'Switch: failed to add layout elements',
      });
      return false;
    }

    const resultElements = (
      command as AddLayoutElementsCommand
    ).getResultElements();
    if (!resultElements) {
      VideoEditorLogger.logCommand(this, 'error', {
        error: 'Switch: failed to get result elements',
      });
      return false;
    }

    this._resultElements = {
      top: resultElements[0],
      bottom: resultElements[1],
      parent: this.sourceElement,
    };
    this.canUndo = true;

    return true;
  }

  getDescription(): string {
    return `switching layout type`;
  }

  getResultElements(): ResultElements | null {
    if (!this._resultElements) {
      return null;
    }
    return this._resultElements;
  }
}
